import { Status, StatusCode } from 'api/grpc/resources';
import {
  Annotation,
  Concept,
  Data,
  Filter,
  Input,
  RegionInfo,
  Image,
  Video,
  Audio,
  Text,
  Worker,
  User,
  Model,
  ModelVersion,
} from 'clarifai-web-grpc/proto/clarifai/api/resources_pb';
import { Struct } from 'google-protobuf/google/protobuf/struct_pb';
import { partition } from 'rambda';
import { AnnotationStatus } from 'modules/Annotations/store/annotationsStore';
import { ConceptFilter, FilterOperator } from 'modules/ConceptFilterStore';
import { DatasetFilter } from 'modules/InputsManager/models/DatasetFiltersStore';
import { InputTypeFilter } from 'modules/InputsManager/models/InputsTypeFiltersStore';
import { WorkerData } from 'modules/InputsManager/models/LabelerFilterStore';

export interface InputThumbnailProps {
  input: Input;
  score?: number;
  onLoad: React.EventHandler<React.SyntheticEvent>;
}

export interface AnnotationThumbnailProps {
  isHovered?: boolean;
  onLoad: React.EventHandler<React.SyntheticEvent>;
  src?: string;
  origSrc?: string;
  regionInfo?: RegionInfo | null;
}
interface PartitionNegatedFilters {
  <TFilter extends { negate: boolean }>(filters: TFilter[]): [TFilter[], TFilter[]];
}

export type SmartMenuItem = {
  id: string;
  text: string;
  notTrained?: boolean;
};

// In future, we can add more default filters here
// For now, we only have status filter
export const defaultFiltersValidator = [
  {
    checker: (filter: Filter) => filter?.getAnnotation()?.getStatus()?.getCode() === StatusCode.ANNOTATION_SUCCESS,
  },
];

/** Split filter array into two, one negated and one not */
const partitionNegatedFilters: PartitionNegatedFilters = partition(({ negate }) => negate);

const createConceptFilter = (conceptFilters: { id: string }[], { value } = { value: 1 }): Filter => {
  const concepts = conceptFilters.map((filter) => {
    const concept = new Concept();
    concept.setId(filter.id);
    concept.setValue(value);
    return concept;
  });

  const data = new Data();
  data.setConceptsList(concepts);

  const annotation = new Annotation();
  annotation.setData(data);

  const filter = new Filter();
  filter.setAnnotation(annotation);

  return filter;
};

const createInputTypeFilter = (inputTypeFilters: { id: string }[]): Filter => {
  const input = new Input();
  const filter = new Filter();
  const data = new Data();
  const selectedInputTypeData = inputTypeFilters.map((inputFilter) => {
    if (inputFilter.id === 'image') {
      const image = new Image();

      data.setImage(image);
    }

    if (inputFilter.id === 'video') {
      const video = new Video();
      data.setVideo(video);
    }

    if (inputFilter.id === 'audio') {
      const audio = new Audio();
      data.setAudio(audio);
    }
    if (inputFilter.id === 'text') {
      const text = new Text();
      data.setText(text);
    }

    input.setData(data);
    return input;
  });

  selectedInputTypeData.forEach((inputs) => {
    filter.setInput(inputs);
  });

  return filter;
};

const createDatasetFilter = (datasetFilters: { id: string }[]): Filter => {
  const input = new Input();
  input.setDatasetIdsList(datasetFilters.map((filter) => filter.id));

  const filter = new Filter();
  filter.setInput(input);

  return filter;
};

const createStatusFilter = (statusFilterValue: AnnotationStatus): Filter => {
  const status = new Status();
  const annotation = new Annotation();

  if (statusFilterValue === AnnotationStatus.Success) {
    status.setCode(StatusCode.ANNOTATION_SUCCESS);
  }
  if (statusFilterValue === AnnotationStatus.AwaitingReview) {
    status.setCode(StatusCode.ANNOTATION_AWAITING_REVIEW);
  }
  if (statusFilterValue === AnnotationStatus.AwaitingConsensusReview) {
    status.setCode(StatusCode.ANNOTATION_AWAITING_CONSENSUS_REVIEW);
  }
  if (statusFilterValue === AnnotationStatus.Pending) {
    status.setCode(StatusCode.ANNOTATION_PENDING);
  }
  annotation.setStatus(status);
  const filter = new Filter();
  filter.setAnnotation(annotation);
  return filter;
};

const createLabelerFilter = (labelerFilterValue: WorkerData): Filter => {
  const annotation = new Annotation();
  const worker = new Worker();
  if (labelerFilterValue.type === 'user') {
    const user = new User();
    user.setId(labelerFilterValue.id);
    worker.setUser(user);
    annotation.setWorker(worker);
  } else if (labelerFilterValue.type === 'model') {
    const model = new Model();
    const modelVersion = new ModelVersion();
    modelVersion.setId(labelerFilterValue.id);
    model.setModelVersion(modelVersion);
    worker.setModel(model);
    annotation.setWorker(worker);
  }
  // TODO: create task
  // This is commented out because we need this code when we upgrade clarifai-web-grpc to 9.8
  // else {
  //   annotation.setWorkflowVersionId(labelerFilterValue.id);
  // }
  const filter = new Filter();
  filter.setAnnotation(annotation);
  return filter;
};
interface CompileApiFilters {
  ({
    metadataFilter,
    unlabeledFilter,
    unassignedFilter,
    conceptFilters,
    datasetFilters,
    inputTypeFilters,
    conceptsFilterOperator,
    allSearch,
    statusFilterValue,
    labelerFilterValue,
    allowStatusFilterForAnnotation,
  }: {
    metadataFilter?: CF.API.Metadata | undefined;
    unlabeledFilter?: string[];
    unassignedFilter?: string[];
    conceptFilters?: ConceptFilter[];
    datasetFilters?: DatasetFilter[];
    inputTypeFilters?: InputTypeFilter[];
    conceptsFilterOperator?: FilterOperator;
    allSearch?: boolean;
    statusFilterValue: AnnotationStatus;
    labelerFilterValue: WorkerData;
    allowStatusFilterForAnnotation?: boolean;
  }): Filter[];
}

export const compileAPIFilters: CompileApiFilters = ({
  metadataFilter,
  unlabeledFilter,
  unassignedFilter,
  conceptFilters,
  datasetFilters,
  inputTypeFilters,
  conceptsFilterOperator,
  allSearch,
  statusFilterValue,
  labelerFilterValue,
  allowStatusFilterForAnnotation = true,
}) => {
  const filters = [];

  if (metadataFilter !== undefined) {
    const data = new Data();
    data.setMetadata(Struct.fromJavaScript(metadataFilter));

    const annotation = new Annotation();
    annotation.setData(data);

    const filter = new Filter();
    filter.setAnnotation(annotation);

    filters.push(filter);
  }

  if (unlabeledFilter !== undefined && unlabeledFilter.length > 0) {
    // filters out each input containing any concept in conceptsFilter
    unlabeledFilter.forEach((conceptId) => {
      const filter = createConceptFilter([{ id: conceptId }]);

      filter.setNegate(true); // to negate is to filter 'out' the input
      filters.push(filter);
    });
  }

  if (inputTypeFilters !== undefined && inputTypeFilters.length > 0) {
    const grpcInputTypeFilter = createInputTypeFilter(inputTypeFilters);
    filters.push(grpcInputTypeFilter);
  }

  if (unassignedFilter !== undefined && unassignedFilter.length > 0) {
    const filter = new Filter();
    filter.setNegate(true);

    const input = new Input();

    unassignedFilter.forEach((datasetId) => {
      input.addDatasetIds(datasetId);
      filter.setInput(input);
    });

    filters.push(filter);
  }

  if (conceptFilters && conceptFilters?.length > 0) {
    const [negatedConceptFilters, normalConceptFilters] = partitionNegatedFilters(conceptFilters);

    if (normalConceptFilters.length > 0) {
      const normalFilter =
        conceptsFilterOperator === FilterOperator.OR
          ? [createConceptFilter(normalConceptFilters)]
          : normalConceptFilters.map((filter) => createConceptFilter([filter]));

      filters.push(...normalFilter);
    }

    if (negatedConceptFilters.length > 0) {
      const negatedFilter = createConceptFilter(negatedConceptFilters);
      negatedFilter.setNegate(true);

      filters.push(negatedFilter);
    }
  }

  if (datasetFilters && datasetFilters.length > 0) {
    const [negatedDatasetFilters, normalDatasetFilters] = partitionNegatedFilters(datasetFilters);
    const normalFilter = createDatasetFilter(normalDatasetFilters);
    const negatedFilter = createDatasetFilter(negatedDatasetFilters);
    negatedFilter.setNegate(true);

    if (normalDatasetFilters.length > 0) filters.push(normalFilter);
    if (negatedDatasetFilters.length > 0) filters.push(negatedFilter);
  }

  if (statusFilterValue && allowStatusFilterForAnnotation) {
    const statusFilter = createStatusFilter(statusFilterValue);
    filters.push(statusFilter);
  }

  if (labelerFilterValue && labelerFilterValue.type !== 'selectAll') {
    const labelerFilter = createLabelerFilter(labelerFilterValue);
    filters.push(labelerFilter);
  }

  if (allSearch) {
    const input = new Input();
    const status = new Status();
    status.setCode(StatusCode.INPUT_DOWNLOAD_SUCCESS);
    input.setStatus(status);
    const filter = new Filter();
    filter.setInput(input);

    filters.push(filter);
  }

  return filters;
};

export const convertConceptToSmartMenuItem = (concept: Concept): SmartMenuItem => ({
  id: concept.getId(),
  // Smart menu items must only have dashes as special characters.
  text: convertSpacesToDashes(concept.getName()),
});

export const convertConceptToNotTrainedSmartMenuItem = (concept: Concept): SmartMenuItem => ({
  id: concept.getId(),
  // Smart menu items must only have dashes as special characters.
  text: `${convertSpacesToDashes(concept.getName())} (not trained)`,
  notTrained: true,
});

const convertSpacesToDashes = (str: string) => {
  const REG_EXP = /\s/g;

  return str.replace(REG_EXP, '-');
};

export const convertDashesToSpaces = (str: string) => {
  const REG_EXP = /-/g;

  return str.replace(REG_EXP, ' ');
};

export const getImageSrcFromBase64 = (base64: string) => {
  return base64?.includes('data:image') ? base64 : `data:image/png;base64,${base64}`;
};
export const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export const chunkInputIds = (ids: string[], size: number): string[][] => {
  return ids.reduce((chunks, id, index) => {
    const chunkIndex = Math.floor(index / size);
    chunks[chunkIndex] = [...(chunks[chunkIndex] || []), id];
    return chunks;
  }, [] as string[][]);
};

// This function checks if the filters are the default ones
// If they are, we don't need to show the 'Clear filters'
// For Example, right now ANNOTATION_SUCCESS is a default filter
// It will be present in filter array if the user doesn't change the status filter
export const checkDefaultFilters = (filters: Filter[]): boolean => {
  for (const filter of filters) {
    let success = false;
    for (const defaultFilter of defaultFiltersValidator) {
      success = defaultFilter.checker(filter as Filter);
      if (!success) {
        return false;
      }
    }
  }
  return true;
};
